import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { CardFilter } from "@ptg-processing/types/models/card-filter.model";
import { ChipData } from "@ptg-shared/controls/view-current-filter/view-current-filter.component";


@Component({
  selector: 'ptg-card-filters',
  templateUrl: './card-filters.component.html',
  styleUrls: ['./card-filters.component.scss']
})
export class CardFiltersComponent implements OnInit, OnChanges {
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.cardFilters) {
      this.transformedCardFilters = this.transformCardFilters(this.cardFilters);
      this.listChipData = this.getGroupedChipData(this.transformedCardFilters);
    }
  }

  ngOnInit(): void {
  }

  listChipData: ChipData[] = [];

  @Input() cardFilters: CardFilter[] = [];

  transformedCardFilters: CardFilter[] = [];

  @Output() removed: EventEmitter<{ chipData: ChipData, isLastCard: boolean }> =
    new EventEmitter<{ chipData: ChipData, isLastCard: boolean }>();

  @Input() canRemove: boolean = false;

  getGroupedChipData(cardFilters: CardFilter[]): ChipData[] {
    const groupedFilters = new Map<string, CardFilter[]>();

    cardFilters.forEach(filter => {
      const key = `${filter.groupId}`;
      if (!groupedFilters.has(key)) {
        groupedFilters.set(key, []);
      }
      groupedFilters.get(key)!.push(filter);
    });

    const chipDataList: ChipData[] = [];
    groupedFilters.forEach((filters, key) => {
      if (filters.length > 0) {
        chipDataList.push(this.getChipData(filters, key));
      }
    });

    return chipDataList;
  }

  getChipData(filters: CardFilter[], groupId: string): ChipData {
    const firstFilter = filters[0];
    const contents: any[] = [];

    contents.push({ value: `${firstFilter.cardName}:`, className: 'font-bold' });

    filters.forEach((filter, index) => {
      if (index > 0) {
        contents.push({ value: ', ', className: 'normal' });
      }

      contents.push({ value: filter.propertyName ?? '', className: 'normal' });


      if (filter.configs?.componentType === 'dateRange' && filter.displayValue?.includes('|')) {
        const [fromDate, toDate] = filter.displayValue.split('|');
        contents.push({ value: 'is', className: 'italic' });
        contents.push({ value: 'between', className: 'italic' });
        contents.push({ value: fromDate, className: 'colored' });
        contents.push({ value: 'and', className: 'italic' });
        contents.push({ value: toDate, className: 'colored' });
      } else {
        contents.push({ value: 'is', className: 'italic' });
        contents.push({ value: filter.displayValue ?? '', className: 'colored' });
      }
    });

    return {
      contents: contents,
      groupId: groupId,
      cardId: firstFilter.cardId
    }
  }

  removeFilter(filter: ChipData, index: number) {
    this.listChipData.splice(index, 1);
    const isLastCard = this.listChipData.findIndex(item => item.cardId === filter.cardId) === -1
    this.removed.emit({ chipData: filter, isLastCard });
  }

  transformCardFilters(cardFilters: CardFilter[]): CardFilter[] {
    const transformedFilters: CardFilter[] = [];

    const groupedByGroupId = new Map<string, CardFilter[]>();

    cardFilters.forEach(filter => {
      const groupId = filter.groupId ?? '';
      if (!groupedByGroupId.has(groupId)) {
        groupedByGroupId.set(groupId, []);
      }
      groupedByGroupId.get(groupId)!.push(filter);
    });

    groupedByGroupId.forEach((filters, groupId) => {
      const datePickerFilters: CardFilter[] = [];
      const otherFilters: CardFilter[] = [];

      filters.forEach(filter => {
        if (filter.configs?.componentType === 'datePicker') {
          datePickerFilters.push(filter);
        } else {
          otherFilters.push(filter);
        }
      });

      transformedFilters.push(...otherFilters);
      if (datePickerFilters.length > 0) {
        const datePickerGroups = new Map<string, CardFilter[]>();

        datePickerFilters.forEach(filter => {
          const propertyBaseName = this.getDatePropertyBaseName(filter.propertyName);
          const groupKey = `${filter.cardId}_${propertyBaseName}`;

          if (!datePickerGroups.has(groupKey)) {
            datePickerGroups.set(groupKey, []);
          }
          datePickerGroups.get(groupKey)!.push(filter);
        });

        datePickerGroups.forEach((dateFilters, groupKey) => {
          if (dateFilters.length === 2) {
            const fromFilter = dateFilters.find(f => f.propertyName?.toLowerCase().includes('from')) || dateFilters[0];
            const toFilter = dateFilters.find(f => f.propertyName?.toLowerCase().includes('to')) || dateFilters[1];

            if (fromFilter.displayValue && toFilter.displayValue) {
              const propertyBaseName = this.getDatePropertyBaseName(fromFilter.propertyName);
              transformedFilters.push({
                ...fromFilter,
                groupId: groupId,
                propertyName: propertyBaseName,
                value: `${fromFilter.displayValue}_${toFilter.displayValue}`,
                displayValue: `${fromFilter.displayValue}|${toFilter.displayValue}`,
                configs: {
                  ...fromFilter.configs,
                  componentType: 'dateRange'
                }
              });
            }
          } else {
            transformedFilters.push(...dateFilters);
          }
        });
      }
    });

    return transformedFilters;
  }

  private getDatePropertyBaseName(propertyName: string): string {
    return propertyName
      .replace(/\s+(from|to)$/i, '')
      .replace(/\s+(start|end)$/i, '')
      .trim();
  }
}